import { Chip, List, MenuItem, Paper, Popover, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setCurrentStageName, setStageData } from "../../reducers/rtfoSlice";
import { useSocket } from "../../context/useSocket";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    marginTop: 0,
    marginBottom: 0,
    border: 'none'
  },
});

export const RTFOStageIsolationDataTable = ({ currentRTFO, darkMode }) => {

    // STYLES
    const classes = useStyles();
    const dispatch = useDispatch();

    const rows = useMemo(() => {
      if (!currentRTFO?.stages?.length > 0) return [];
      let stages = [];

      currentRTFO?.stages?.forEach(stageName => {
        const stage = currentRTFO?.stageData?.[stageName];

        stages.push({
          id: stageName,
          stageStartTimestamp: stage?.stageStartTimestamp,
          stageName,
          wellName: stage?.wellName,
          wellUwi: stage?.wellUwi,
          stageNumber: stage?.stageNumber,
          prevZeroProppantFracfeed: +(stage?.pumpdownParams?.prevZeroProppantFracfeed?.toFixed(2)) || 0,
          currentPreSandFracfeed: +(stage?.pumpdownParams?.currentPreSandFracfeed?.toFixed(2)) || 0,
          fracfeedIsolation: stage?.pumpdownParams?.stageIsolation?.fracfeedIsolation || 'N/A',
          pumpdownIsolation: stage?.pumpdownParams?.stageIsolation?.pumpdownIsolation || 'N/A',
          pressureTestIsolation: stage?.pumpdownParams?.stageIsolation?.pressureTestIsolation || 'N/A',
          postPlugIsolation: stage?.pumpdownParams?.stageIsolation?.postPlugIsolation || 'N/A',
          postPerfIsolation: stage?.pumpdownParams?.stageIsolation?.postPerfIsolation || 'N/A'
        });

      });

      stages = stages.sort((a, b) => b.stageStartTimestamp - a.stageStartTimestamp);

      return stages;
    }, [currentRTFO?.stageData, currentRTFO?.stages]);
  
  try {

    const columns = [
      {
        field: 'stageName',
        headerName: 'Stage Name',
        width: 175,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellName',
        headerName: 'Well Name',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellUwi',
        headerName: 'Well UWI',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'stageNumber',
        headerName: 'Stage Number',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'prevZeroProppantFracfeed',
        headerName: 'Prev. Fracfeed',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'currentPreSandFracfeed',
        headerName: 'Curr. Fracfeed',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number'
      },
      {
        field: 'fracfeedIsolation',
        headerName: 'Fracfeed Isolation',
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: (params) => 
          <DropdownChip currentRTFO={currentRTFO} options={['Pass', 'Fail', 'N/A']} param={'fracfeedIsolation'} stageName={params?.id} prevValue={params?.value} dispatch={dispatch} darkMode={darkMode} />
      },
      {
        field: 'pumpdownIsolation',
        headerName: 'Pumpdown Isolation',
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: (params) => 
          <DropdownChip currentRTFO={currentRTFO} options={['Pass', 'Fail', 'N/A']} param={'pumpdownIsolation'} stageName={params?.id} prevValue={params?.value} dispatch={dispatch} darkMode={darkMode} />
      },
      {
        field: 'pressureTestIsolation',
        headerName: 'Pressure Test Isolation',
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: (params) => 
          <DropdownChip currentRTFO={currentRTFO} options={['Pass', 'Fail', 'N/A']} param={'pressureTestIsolation'} stageName={params?.id} prevValue={params?.value} dispatch={dispatch} darkMode={darkMode} />
      },
      {
        field: 'postPlugIsolation',
        headerName: 'Post Plug Isolation',
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: (params) => 
          <DropdownChip currentRTFO={currentRTFO} options={['Pass', 'Fail', 'N/A']} param={'postPlugIsolation'} stageName={params?.id} prevValue={params?.value} dispatch={dispatch} darkMode={darkMode} />
      },
      {
        field: 'postPerfIsolation',
        headerName: 'Post Plug Isolation',
        width: 125,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: (params) => 
          <DropdownChip currentRTFO={currentRTFO} options={['Pass', 'Fail', 'N/A']} param={'postPerfIsolation'} stageName={params?.id} prevValue={params?.value} dispatch={dispatch} darkMode={darkMode} />
      }
    ];

    return (
      <Paper className={classes.root} elevation={0}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10,25,50,100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: GridToolbar,
          }}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: darkMode ? '#263238' : 'whitesmoke'
            },
            '& .super-app-theme--Current': {
              fontWeight: 'bold',
              backgroundColor: 'rgba(7, 88, 183, 0.25)' // highlight the current row
            }
          }}
          style={{fontSize: '12px'}}
          getRowClassName={(params) => {
            return (params?.row?.stageName === currentRTFO?.currentStageName) ? `super-app-theme--Current` : '';
          }}
          onRowClick={params => (!currentRTFO?.isLoadingData && dispatch(setCurrentStageName(params?.row?.stageName)))}
          density="compact"
        />
      </Paper>
    );
  } catch (error) {
    return <></>
  };
};

const DropdownChip = ({ currentRTFO, options, stageName, param, prevValue, padName, dispatch, darkMode }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const { socket } = useSocket();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'dropdown-popover' : undefined;

  return (
    <>
      <Chip
        size="small"
        label={prevValue}
        onClick={handleClick}
        onDelete={handleClick} // Optional: Trigger dropdown on delete icon click
        deleteIcon={<ExpandMore style={{color: 'black'}} />}
        style={{fontSize: 12, color: 'black', backgroundColor: prevValue === 'Pass' ? '#d6f5d6' : prevValue === 'Fail' ? '#ffd6cc' : 'lightgrey'}}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <List>
          {options.map((option, index) => (
            <MenuItem key={index} selected={option === prevValue} onClick={() => handleValueChange(param, option, prevValue, stageName, padName)}><Typography>{option}</Typography></MenuItem>
          ))}
        </List>
      </Popover>
    </>
  );

  function handleValueChange(param, value, prevValue, stageName) {
    if (value !== prevValue) {
      console.log(`Updating ${stageName} ${param} to ${value}`);

      const stageData = {...currentRTFO?.stageData?.[stageName]};
      const newStageIsolation = {...stageData?.pumpdownParams?.stageIsolation || {}};
      newStageIsolation[param] = value;

      stageData.pumpdownParams = {
        ...stageData.pumpdownParams,
        stageIsolation: newStageIsolation
      };

      dispatch(setStageData({stageData}));

      socket?.send(JSON.stringify({
        messageType: 'savePumpdownParams',
        stageName: stageName,
        pumpdownParams: stageData.pumpdownParams
      }));
    };
    handleClose();
  };
};